// Screens before the dashboard (signing in is in SignIn.tsx): the platform admin's second factor, creating an
// organization, and a suspended organization.
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import type { MfaState } from "../lib/api";
import { TIMEZONES } from "../lib/settings";
import { Brand, DemoBanner } from "../app/Layout";
import { useSession } from "../app/session";
import { errorMessage, ErrorNote, Field, Loading } from "../app/ui";

function AuthCard({ children }: { children: ReactNode }) {
  const { api, signedIn } = useSession();
  return (
    <>
      {api.demo && signedIn && <DemoBanner />}
      <div className="auth">
        <div className="card">
          <Brand />
          {children}
        </div>
      </div>
    </>
  );
}

/** The platform admin's second factor: set up an authenticator app once, then a code at each sign-in (PLAT-03). */
export function Mfa() {
  const { api, reload, signOut } = useSession();
  const [state, setState] = useState<MfaState | null>(null);
  const [enrolment, setEnrolment] = useState<
    { factorId: string; qr: string; secret: string } | null
  >(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.mfaState().then(setState, (e) => setError(errorMessage(e)));
  }, [api]);

  const factorId = enrolment?.factorId ?? state?.verifiedFactorId ?? null;

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    setBusy(true);
    setError(null);
    try {
      await api.mfaVerify(factorId, code);
      await reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const enrol = async () => {
    setBusy(true);
    setError(null);
    try {
      setEnrolment(await api.mfaEnroll());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard>
      <h1>Second step</h1>
      <ErrorNote error={error} />
      {!state && !error && <Loading />}
      {state && !factorId && !enrolment && (
        <>
          <p className="muted">
            The platform console needs an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password or
            similar) in addition to your email code.
          </p>
          <button
            className="primary"
            disabled={busy}
            onClick={() => void enrol()}
          >
            Set up an authenticator app
          </button>
        </>
      )}
      {enrolment && (
        <>
          <p>
            Scan this code with your authenticator app, then enter the 6-digit code it shows.
          </p>
          <img
            className="qr"
            src={enrolment.qr}
            alt="QR code for your authenticator app"
          />
          <p className="faint">
            Can't scan? Enter this key: <span className="code">{enrolment.secret}</span>
          </p>
        </>
      )}
      {factorId && (
        <form onSubmit={verify}>
          {!enrolment && (
            <p className="muted">
              Enter the 6-digit code from your authenticator app.
            </p>
          )}
          <Field label="Code">
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
          </Field>
          <button className="primary" disabled={busy || code.trim().length < 6}>
            {busy ? "Checking…" : "Continue"}
          </button>
        </form>
      )}
      <p style={{ marginTop: 16 }}>
        <button className="link" onClick={() => void signOut()}>
          Sign out
        </button>
      </p>
    </AuthCard>
  );
}

export function Onboarding() {
  const { api, reload, signOut } = useSession();
  const [name, setName] = useState("");
  const [you, setYou] = useState("");
  const [tz, setTz] = useState("Asia/Kolkata");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createOrganization({
        name: name.trim(),
        displayName: you.trim(),
        timezone: tz,
      });
      await reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard>
      <h1>Set up your company</h1>
      <p className="muted">
        You'll be its owner. Next, invite your telecallers by phone number; they install the app and sign in with a
        code.
      </p>
      <ErrorNote error={error} />
      <form onSubmit={submit}>
        <Field label="Company name">
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sharma Realty"
          />
        </Field>
        <Field label="Your name">
          <input
            required
            value={you}
            onChange={(e) => setYou(e.target.value)}
            placeholder="Anil Sharma"
          />
        </Field>
        <Field
          label="Time zone"
          hint="Follow-ups and working hours use this time zone."
        >
          <select value={tz} onChange={(e) => setTz(e.target.value)}>
            {TIMEZONES.map((z) => <option key={z}>{z}</option>)}
          </select>
        </Field>
        <button
          className="primary"
          disabled={busy || !name.trim() || !you.trim()}
        >
          {busy ? "Creating…" : "Create company"}
        </button>
      </form>
      <p className="faint" style={{ marginTop: 16 }}>
        Joining an existing company? Ask its owner to invite the email or phone you signed in with, then sign in again.
        {" "}
        <button className="link" onClick={() => void signOut()}>
          Sign out
        </button>
      </p>
    </AuthCard>
  );
}

export function Suspended() {
  const { status, signOut } = useSession();
  const m = status?.member;
  return (
    <AuthCard>
      <h1>{m?.org_name} is paused</h1>
      <p>
        Voxowa has paused this account{m?.suspended_reason ? `: ${m.suspended_reason}` : "."}
      </p>
      <p className="muted">
        Calls are not being processed and the dashboard is closed until it is reactivated. Nothing has been deleted.
        Please contact Voxowa support.
      </p>
      <button onClick={() => void signOut()}>Sign out</button>
    </AuthCard>
  );
}

export function Problem({ error }: { error: string }) {
  const { reload, signOut } = useSession();
  return (
    <AuthCard>
      <h1>Something went wrong</h1>
      <ErrorNote error={error} />
      <div className="row">
        <button className="primary" onClick={() => void reload()}>
          Try again
        </button>
        <button onClick={() => void signOut()}>Sign out</button>
      </div>
    </AuthCard>
  );
}
